export interface BaseMessage {
    type: string;
    event_id: string;
}

/*                      */
export interface InitialMessage extends BaseMessage {
    type: "initial.message";
    message: string;
}

/*                                   */
export interface CompletableMessage extends BaseMessage {
    message_completed: boolean;
    event_completed: boolean;
}

/*                                         */
export interface UserMessage extends BaseMessage {
    type: "user.message";
    message: string;
}

/*                                          */
export interface MemoryMessage extends BaseMessage {
    type: "user.memory";
    messages: object[];
}

/*                                         */
export interface AssistantMessage extends CompletableMessage {
    type: "assistant.message";
    message: string;
    quick_response_options?: string[];
    meta?: {
        detected_product_base_classes?: string[];
    };
}

/*                                   */
export interface AssistantError extends CompletableMessage {
    type: "assistant.error";
    message: string;
}

/*                                                        */
export interface AssistantOWASPError extends CompletableMessage {
    type: "assistant.error.owasp_violation";
    message: string;
}

/*                                  */
export interface AssistantProductList extends CompletableMessage {
    type: "assistant.product_list";
    message: ProductListMessage;
}
export interface AssistantRecommendation extends CompletableMessage {
    type: "assistant.recommendations";
    message: RecommendationMessage;
}

/*                                      */
export interface AssistantProgress extends CompletableMessage {
    type: "assistant.progress";
    message: string;
}

export interface AssistantGuidanceLoaded extends CompletableMessage {
    type: "assistant.guidance_loaded";
    message: string[];
}

export interface RecommendationMessage {
    recommendations: Recommendation[];
    _meta?: ProductsMetaData;
}
export interface Recommendation {
    variationId: string;
    reason: string;
}

export interface ProductListMessage {
    _embedded: {
        item: Array<{
            variationIds: string[];
            productIds: string[];
            adsUrl?: string;
            title: string;
            articleNumber: string;
            brand?: string;
            description?: string;
            price?: number;
            _meta: {
                tracking: TrackingLabels;
                clickTracking: TrackingLabels;
            };
        }>;
    };
    _offsetPage?: {
        offset: number;
        limit: number;
        totalElements: number;
        totalElementsDisplay: number;
    };
    _meta?: ProductsMetaData;
}

export interface TrackingLabels {
    [key: string]: string;
}

export interface ProductsMetaData {
    dresonRule?: string;
    responseContent?: string;
    searchQuery?: string;
    searchParams?: SearchParams;
    tracking: TrackingLabels;
}

export enum TrackingBackendKeys {
    SearchTermNormalized = "wk.san_SearchTermNormalized",
    RankingContext = "wk.san_RankingContext",
    RankingTarget = "wk.san_RankingTarget",
    SearchTermNormalizedUnsorted = "wk.san_SearchTermNormalizedUnsorted",
    SelectionRule = "wk.san_SelectionRule",
}

export enum SanChatBotResponseLabels {
    PRODUCTS_CINEMA = "products_cinema",
    LINK_LIST = "link_list",
    PRODUCTS_ADVISORY = "products_advisory",
    QUICK_RESPONSE = "quick_response",
    TEXT = "text",
    ERROR = "error",
}

export interface Dimension {
    min?: number;
    max?: number;
    unit?: string;
}

export interface SearchParams {
    term?: string;
    descriptiveProperties: string[];
    priceRestriction: Dimension;
    dimensionalRestriction: {
        heightRestriction?: Dimension;
        widthRestriction?: Dimension;
        lengthRestriction?: Dimension;
        depthRestriction?: Dimension;
        diameterRestriction?: Dimension;
    };
}

/*                               */
export type Message =
    | InitialMessage
    | UserMessage
    | AssistantMessage
    | AssistantError
    | AssistantOWASPError
    | AssistantProductList
    | AssistantRecommendation
    | AssistantProgress
    | AssistantGuidanceLoaded;

export interface WebSocketConnection {
    connect(): Promise<void>;

    disconnect(): void;

    send(message: UserMessage): void;

    onMessage(callback: (message: Message) => void): void;

    onError(callback: (error: Event) => void): void;

    onClose(callback: (event: CloseEvent) => void): void;

    onOpen(callback: () => void): void;

    isConnected(): boolean;
}

/*                                                */
export type MessageHandler = {
    onTextMessage: (text: string, messageData: AssistantMessage) => void;
    onProductList: (messageData: AssistantProductList) => void;
    onRecommendation: (messageData: AssistantRecommendation) => void;
    onProgress: (progressText: string, messageData: AssistantProgress) => void;
    onGuidanceLoaded: (messageData: AssistantGuidanceLoaded) => void;
    onError: (errorText: string, messageData: AssistantError | AssistantOWASPError) => void;
    onEventCompleted: () => void;
    onAudioChunk?: (chunk: ArrayBuffer) => void;
};

/*                                */
/*                                                                                                          */
export function isCompletableMessage(message: Message): message is CompletableMessage {
    const m = message as CompletableMessage;
    return m.message_completed !== undefined && m.event_completed !== undefined;
}

/*                                                */
export function isMessageCompleted(message: CompletableMessage): boolean {
    return message.message_completed;
}

/*                                      */
export function isEventCompleted(message: CompletableMessage): boolean {
    return message.event_completed;
}
